<!-- src/components/forms/completed/EyesightDeclarationCompleted.vue -->
<template>
  <div class="space-y-12 animate-fadeIn">
    
    <!-- 1. Identity Snapshot -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <IdentificationIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Personnel Identity</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="StatCard bg-white">
          <span class="label">First Name</span>
          <span class="value uppercase">{{ submissionData.firstName }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Last Name</span>
          <span class="value uppercase">{{ submissionData.lastName }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Verified Email</span>
          <span class="value lowercase text-xs">{{ submissionData.email }}</span>
        </div>
      </div>
    </section>

    <!-- 2. Vision Standards Checklist -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <EyeIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Visual Capability Assessment</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-for="q in visionQuestions" :key="q.key" 
             class="flex items-center justify-between p-6 bg-white border border-slate-200 rounded-[28px] shadow-sm hover:border-[#20FCF1]/30 transition-all">
          <p class="text-xs font-bold text-slate-700 uppercase tracking-tight">{{ q.label }}</p>
          <span :class="getStatusClass(submissionData[q.key])" class="px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-sm">
            {{ formatYesNoNA(submissionData[q.key]) }}
          </span>
        </div>

        <!-- Conditional Spare Glasses Check -->
        <div v-if="submissionData.wearsContactLenses === true" 
             class="flex items-center justify-between p-6 bg-[#EBFDF8] border border-[#20FCF1]/20 rounded-[28px] shadow-sm md:col-span-2 animate-fadeIn">
          <div class="flex items-center gap-3">
            <div class="bg-[#20FCF1] p-2 rounded-full shadow-sm">
              <CheckBadgeIcon class="h-4 w-4 text-[#0A1630]" />
            </div>
            <p class="text-xs font-black text-[#0A1630] uppercase tracking-widest">Carries spare glasses while on site</p>
          </div>
          <span :class="getStatusClass(submissionData.carriesSpareGlasses)" class="px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-sm">
            {{ formatYesNoNA(submissionData.carriesSpareGlasses) }}
          </span>
        </div>
      </div>
    </section>

    <!-- 3. Final Attestation -->
    <section class="border-t pt-12">
      <div class="flex flex-col md:flex-row justify-between items-end gap-8">
        <div class="space-y-4">
          <div class="flex items-center gap-2">
            <ShieldCheckIcon class="h-5 w-5 text-[#20FCF1]" />
            <p class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Visual Compliance Verified</p>
          </div>
          <div class="p-6 bg-slate-900 rounded-[32px] border border-slate-800 max-w-sm">
            <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Confirmation</p>
            <p class="text-xs text-slate-300 font-medium italic">"I formally confirm that the visual standards information provided is true and correct to the best of my knowledge."</p>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl || submissionData.signatureUrl" class="bg-white p-6 rounded-[32px] border border-slate-200 shadow-xl flex flex-col items-center min-w-[260px]">
          <p class="text-[9px] font-black uppercase text-slate-400 mb-4 tracking-widest">Official Record Seal</p>
          <img :src="submissionData.signatureDataUrl || submissionData.signatureUrl" class="max-h-20 object-contain mb-4" />
          <div class="w-full h-px bg-slate-100 mb-4"></div>
          <div class="flex flex-col items-center">
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">{{ submissionData.firstName }} {{ submissionData.lastName }}</span>
            <span class="text-[9px] font-bold text-slate-400 mt-1 uppercase">{{ formatDate(submissionData.submittedAt) }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { 
  IdentificationIcon, EyeIcon, CheckBadgeIcon, ShieldCheckIcon 
} from '@heroicons/vue/24/outline';

defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatYesNoNA: { type: Function, required: true },
  getStatusClass: { type: Function, required: true }
});

const visionQuestions = [
  { label: 'Do you wear glasses?', key: 'wearsGlasses' },
  { label: 'Required for vision standard?', key: 'needsGlassesForStandard' },
  { label: 'Do you wear contact lenses?', key: 'wearsContactLenses' }
];
</script>

<style scoped>
.StatCard { @apply flex flex-col p-6 rounded-[28px] border border-slate-100 shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>